/**
 * Admin Newsletter Analytics Tab
 * View email tracking stats and campaign performance
 */
import { useState, useEffect } from "react";
import { 
  Mail, Eye, MousePointer, TrendingUp, BarChart3, 
  Loader2, ExternalLink, Calendar, Users, Percent
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useAuth } from "../../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNewsletterAnalyticsTab = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [newsletters, setNewsletters] = useState([]);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [newsletterStats, setNewsletterStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    fetchOverview();
    fetchNewsletters();
  }, []);

  const fetchOverview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tracking/overview`, {
        credentials: 'include'
      });
      const data = await res.json();
      setOverview(data);
    } catch (err) {
      console.error("Failed to fetch overview:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsletters = async () => {
    try {
      const res = await fetch(`${API_URL}/api/newsletter/sent`, {
        credentials: 'include'
      });
      const data = await res.json();
      setNewsletters(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch newsletters:", err);
    }
  };

  const fetchNewsletterStats = async (newsletterId) => {
    setLoadingStats(true);
    try {
      const res = await fetch(`${API_URL}/api/tracking/stats/${newsletterId}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setNewsletterStats(data);
      setSelectedNewsletter(newsletterId);
    } catch (err) {
      console.error("Failed to fetch newsletter stats:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-newsletter-analytics-tab">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Mail className="w-8 h-8 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold text-stone-900">{overview?.total_newsletters || 0}</p>
            <p className="text-sm text-stone-500">Campaigns Sent</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold text-stone-900">{overview?.total_emails_sent || 0}</p>
            <p className="text-sm text-stone-500">Total Emails</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Eye className="w-8 h-8 mx-auto text-purple-500 mb-2" />
            <p className="text-2xl font-bold text-stone-900">{overview?.average_open_rate || 0}%</p>
            <p className="text-sm text-stone-500">Avg Open Rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <MousePointer className="w-8 h-8 mx-auto text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-stone-900">{overview?.average_click_rate || 0}%</p>
            <p className="text-sm text-stone-500">Avg Click Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Engagement Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Total Engagement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">{overview?.total_unique_opens || 0}</p>
              <p className="text-sm text-purple-700">Unique Opens</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">{overview?.total_unique_clicks || 0}</p>
              <p className="text-sm text-amber-700">Unique Clicks</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{overview?.recent_opens || 0}</p>
              <p className="text-sm text-blue-700">Recent Opens</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{overview?.recent_clicks || 0}</p>
              <p className="text-sm text-green-700">Recent Clicks</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Campaign Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {newsletters.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Mail className="w-12 h-12 mx-auto text-stone-300 mb-4" />
              <p>No newsletters sent yet</p>
              <p className="text-sm">Send your first newsletter to see analytics here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {newsletters.map(nl => (
                <div 
                  key={nl.newsletter_id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                    selectedNewsletter === nl.newsletter_id 
                      ? "border-amber-500 bg-amber-50" 
                      : "border-stone-200 hover:border-stone-300"
                  }`}
                  onClick={() => fetchNewsletterStats(nl.newsletter_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-stone-800 truncate">{nl.subject}</h4>
                      <div className="flex items-center gap-4 mt-1 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(nl.sent_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {nl.recipient_count} recipients
                        </span>
                        {nl.test_mode && <Badge variant="outline">Test</Badge>}
                        {nl.tracking_enabled && (
                          <Badge className="bg-green-100 text-green-700">Tracking</Badge>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Campaign Stats */}
      {selectedNewsletter && (
        <Card className="border-amber-200">
          <CardHeader className="bg-amber-50">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-amber-600" />
              Campaign Details
              {loadingStats && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {newsletterStats ? (
              <div className="space-y-6">
                {/* Subject */}
                <div>
                  <h4 className="font-semibold text-lg text-stone-800">{newsletterStats.subject}</h4>
                  <p className="text-sm text-stone-500">Sent: {formatDate(newsletterStats.sent_at)}</p>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-3 bg-stone-50 rounded-lg">
                    <p className="text-xl font-bold text-stone-700">{newsletterStats.recipient_count}</p>
                    <p className="text-xs text-stone-500">Sent</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-xl font-bold text-purple-600">{newsletterStats.unique_opens}</p>
                    <p className="text-xs text-purple-700">Unique Opens</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-xl font-bold text-green-600">{newsletterStats.open_rate}%</p>
                    <p className="text-xs text-green-700">Open Rate</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-xl font-bold text-amber-600">{newsletterStats.unique_clicks}</p>
                    <p className="text-xs text-amber-700">Unique Clicks</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-xl font-bold text-blue-600">{newsletterStats.click_through_rate}%</p>
                    <p className="text-xs text-blue-700">CTR</p>
                  </div>
                </div>

                {/* Top Links */}
                {newsletterStats.top_links?.length > 0 && (
                  <div>
                    <h5 className="font-medium text-stone-700 mb-3 flex items-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Top Clicked Links
                    </h5>
                    <div className="space-y-2">
                      {newsletterStats.top_links.map((link, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-2 bg-stone-50 rounded"
                        >
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline truncate max-w-[70%]"
                          >
                            {link.url}
                          </a>
                          <Badge variant="secondary">{link.clicks} clicks</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-stone-500">
                Select a campaign to view detailed stats
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminNewsletterAnalyticsTab;
